import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import AttachmentsWidget from '../components/AttachmentsWidget';

const INSPECTORS = ['SGS', 'Intertek', 'Bureau Veritas', 'DNV', 'CCIC', 'Saybolt', 'Other'];
const INSP_TYPES = [
  { key: 'quality',      label: 'Qualitätsinspektion' },
  { key: 'quantity',     label: 'Mengeninspektion' },
  { key: 'draft_survey', label: 'Draft Survey' },
  { key: 'weight',       label: 'Gewichtszertifikat' },
  { key: 'combined',     label: 'Kombiniert' },
  { key: 'other',        label: 'Sonstige' },
];
const STATUSES = [
  { key: 'pending',     label: 'Ausstehend',  color: 'var(--text3)' },
  { key: 'scheduled',  label: 'Geplant',      color: '#3b82f6' },
  { key: 'in_progress',label: 'Läuft',        color: '#f59e0b' },
  { key: 'completed',  label: 'Abgeschlossen',color: '#22c55e' },
  { key: 'rejected',   label: 'Abgelehnt',    color: '#ef4444' },
];

const EMPTY = {
  deal_reference: '', inspector: '', certificate_number: '', inspection_type: 'quality',
  inspection_date: '', certificate_date: '', quantity_certified: '', quality_grade: '',
  findings: '', port: '', status: 'pending', notes: '',
};

function statusMeta(k) { return STATUSES.find(s => s.key === k) || STATUSES[0]; }
function StatusBadge({ status }) {
  const m = statusMeta(status);
  return (
    <span style={{ display:'inline-flex', padding:'2px 8px', borderRadius:4,
      fontSize:11, fontWeight:700, background:m.color+'22', color:m.color }}>
      {m.label}
    </span>
  );
}

function InspectionModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState(record ? { ...record } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.inspector && !form.certificate_number) { setErr('Inspektor oder Zertifikat-Nr. erforderlich'); return; }
    setSaving(true); setErr('');
    try {
      if (record) await API.put(`/inspections/${record.id}`, form);
      else        await API.post('/inspections', form);
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">{record ? 'Inspektion bearbeiten' : 'Neue Inspektion'}</div>
        {err && <div className="error-msg">{err}</div>}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Inspektor</label>
            <select className="form-input" value={form.inspector} onChange={e => set('inspector', e.target.value)}>
              <option value="">— wählen —</option>
              {INSPECTORS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Zertifikat-Nr.</label>
            <input className="form-input" value={form.certificate_number} onChange={e => set('certificate_number', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Inspektionstyp</label>
            <select className="form-input" value={form.inspection_type} onChange={e => set('inspection_type', e.target.value)}>
              {INSP_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Inspektionsdatum</label>
            <input type="date" className="form-input" value={form.inspection_date} onChange={e => set('inspection_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Zertifikatsdatum</label>
            <input type="date" className="form-input" value={form.certificate_date} onChange={e => set('certificate_date', e.target.value)} />
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label">Zertifizierte Menge</label>
            <input className="form-input" value={form.quantity_certified} onChange={e => set('quantity_certified', e.target.value)} placeholder="z.B. 49,850 MT" />
          </div>
          <div className="form-group">
            <label className="form-label">Qualitätsgrad</label>
            <input className="form-input" value={form.quality_grade} onChange={e => set('quality_grade', e.target.value)} placeholder="z.B. API 33.2, S 1.2%" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Hafen / Port</label>
            <input className="form-input" value={form.port} onChange={e => set('port', e.target.value)} placeholder="z.B. Rotterdam" />
          </div>
          <div className="form-group">
            <label className="form-label">Deal-Referenz</label>
            <input className="form-input" value={form.deal_reference} onChange={e => set('deal_reference', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Befunde / Findings</label>
          <textarea className="form-input" rows={3} value={form.findings} onChange={e => set('findings', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Notizen</label>
          <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InspectionPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [statusFilter, setSt] = useState('');
  const [modal, setModal]     = useState(null);
  const [detail, setDetail]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const r = await API.get('/inspections', { params });
      setRecords(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (detail) {
    return (
      <div className="page-content">
        {modal === 'edit' && (
          <InspectionModal record={detail} onClose={() => setModal(null)}
            onSaved={async () => { const r = await API.get(`/inspections/${detail.id}`); setDetail(r.data); load(); }} />
        )}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>← Zurück</button>
          <h1 style={{ fontSize:18, fontWeight:700 }}>🔬 {detail.inspector || 'Inspektion'} — {detail.certificate_number || INSP_TYPES.find(t=>t.key===detail.inspection_type)?.label}</h1>
          <StatusBadge status={detail.status} />
          <div style={{ marginLeft:'auto' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModal('edit')}>Bearbeiten</button>
          </div>
        </div>
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Zertifikat Details</div>
            {[
              ['Inspektor', detail.inspector],
              ['Zertifikat-Nr.', detail.certificate_number],
              ['Typ', INSP_TYPES.find(t=>t.key===detail.inspection_type)?.label || detail.inspection_type],
              ['Inspektionsdatum', detail.inspection_date],
              ['Zertifikatsdatum', detail.certificate_date],
              ['Zertifizierte Menge', detail.quantity_certified],
              ['Qualitätsgrad', detail.quality_grade],
              ['Hafen', detail.port],
              ['Deal', detail.deal_reference],
            ].filter(([,v]) => v).map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                <span style={{ color:'var(--text3)', minWidth:150 }}>{k}</span>
                <span style={{ color:'var(--text1)' }}>{v}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title">Befunde</div>
            {detail.findings
              ? <p style={{ fontSize:13, color:'var(--text2)', whiteSpace:'pre-wrap', lineHeight:1.6 }}>{detail.findings}</p>
              : <p style={{ fontSize:13, color:'var(--text3)' }}>Keine Befunde erfasst.</p>}
            {detail.notes && <p style={{ fontSize:13, color:'var(--text2)', marginTop:8, whiteSpace:'pre-wrap' }}>{detail.notes}</p>}
          </div>
        </div>
        <div className="card" style={{ marginTop:16 }}>
          <div className="card-title">📎 Anhänge</div>
          <AttachmentsWidget entityType="inspection" entityId={detail.id} />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {modal === 'create' && (
        <InspectionModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>🔬 Inspektionen</h1>
          <p>SGS / Intertek / Bureau Veritas — Qualitäts- & Mengenzertifikate</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ Neue Inspektion</button>
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input className="form-input" style={{ maxWidth:240 }} placeholder="Suche Inspektor, Deal, Zertifikat-Nr.…"
          value={q} onChange={e => setQ(e.target.value)} />
        <select className="form-input" style={{ maxWidth:180 }} value={statusFilter} onChange={e => setSt(e.target.value)}>
          <option value="">Alle Status</option>
          {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>⟳</button>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && records.length === 0 && (
        <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🔬</div>
          <div style={{ fontSize:15, fontWeight:600 }}>Keine Inspektionen erfasst</div>
        </div>
      )}

      <table className="tbl">
        {records.length > 0 && (
          <thead><tr>
            <th>Inspektor</th><th>Typ</th><th>Zertifikat-Nr.</th>
            <th>Deal</th><th>Datum</th><th>Menge</th><th>Status</th><th></th>
          </tr></thead>
        )}
        <tbody>
          {records.map(r => (
            <tr key={r.id} style={{ cursor:'pointer' }} onClick={() => setDetail(r)}>
              <td style={{ fontWeight:600 }}>{r.inspector || '—'}</td>
              <td style={{ color:'var(--text2)' }}>{INSP_TYPES.find(t=>t.key===r.inspection_type)?.label || r.inspection_type}</td>
              <td style={{ fontFamily:'monospace', fontSize:12 }}>{r.certificate_number || '—'}</td>
              <td style={{ color:'var(--text2)' }}>{r.deal_reference || '—'}</td>
              <td style={{ color:'var(--text2)' }}>{r.inspection_date || '—'}</td>
              <td>{r.quantity_certified || '—'}</td>
              <td><StatusBadge status={r.status} /></td>
              <td onClick={e => e.stopPropagation()}>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal(r)}>✏</button>
                  <button className="btn btn-danger btn-sm" onClick={async () => {
                    if (!window.confirm('Löschen?')) return;
                    await API.delete(`/inspections/${r.id}`); load();
                  }}>🗑</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
